import { Link, useLocation, useNavigate } from "react-router-dom"
import ZoomedImage from "../components/ZoomedImage"
import { memo, useContext, useEffect, useState, useMemo } from "react"
import { getArticle, getArticleByUrl, addCategoryNameToArticle, fetchCategories } from "../services/ArticleService"
import '../assets/css/Article.css'
import 'quill/dist/quill.snow.css'
import purify from 'dompurify'
import { Avatar, Button, Form, Input, List, Skeleton } from "antd"
import Comment from "../components/Comment"
import '../assets/css/Prijava.css'
import UserContext from "../context/UserContext"
import { addNewComment } from '../services/CommentService'
import { MODAL_DESCRIPTION_ERROR_ARTICLE_NOT_FOUND, MODAL_DESCRIPTION_ERROR_UNAUTHORIZED, MODAL_DESCRIPTION_SUCCESS_NEW_COMMENT, MODAL_MESSAGE_ERROR, MODAL_MESSAGE_SUCCESS_REQUEST, MODAL_TYPE_ERROR, MODAL_TYPE_SUCCESS } from "../HistoriografijaConstants"
import { useModalContext } from "../context/ModalContext"
import { formatDate, formatDateWithTime, getImageUrl } from "../services/CommonService"
import { setError } from "../context/ArticlesContextActions"
import ArticlesContext from "../context/ArticlesContext"
import { Helmet } from "react-helmet"
import { useCallback } from "react"
import { logoutUser } from "../services/UserService"

function setConditionalCssClasses(data, setJustifyContentContainerClass, setFullWidthDateClass, setAuthorDateJustifyClass) {
    if (data.relatedArticles && data.relatedArticles.length > 3) {
        setJustifyContentContainerClass('space-between')
    } else {
        setJustifyContentContainerClass('less-related-articles')
    }

    if (data.authors && data.authors.length !== 0) {
        setFullWidthDateClass('')
        setAuthorDateJustifyClass('space-between')
    } else {
        setFullWidthDateClass('full-width')
        setAuthorDateJustifyClass('end-justify')
    }
}

const Article = memo(function Article() {
    const location = useLocation() 
    let { state } = location
    const articleId = state?.articleId
    const title = state?.title
    const image = state?.image
    const formattedDate = state?.date
    const categoryName = state?.categoryName
    /** Support for links from old architecture */
    const alreadyFetchedArticle = state?.article

    const [article, setArticle] = useState(alreadyFetchedArticle ? alreadyFetchedArticle : null)
    const [mainImage, setMainImage] = useState(alreadyFetchedArticle ? alreadyFetchedArticle.image : {})
    const [isShowComments, setIsShowComments] = useState(false)
    const [newCommentFieldVisible, setNewCommentFieldVisible] = useState(false)
    const [commentForm] = Form.useForm()
    const [articleCommentsLength, setArticleCommentsLength] = useState(alreadyFetchedArticle ? alreadyFetchedArticle.comments.length : 0)

    const [isLoading, setIsLoading] = useState(alreadyFetchedArticle ? false : true)
    const { userRole, userId, clearUserContext } = useContext(UserContext)
    const {dispatch} = useContext(ArticlesContext)
    const {displayGenericErrorModal, displayPrijavaModal, displayMessageModalWithParameters, resetModalContext} = useModalContext()
    const navigate = useNavigate()

    const [justifyContentContainerClass, setJustifyContentContainerClass] = useState('')
    const [fullWidthDateClass, setFullWidthDateClass] = useState('')
    const [authorDateJustifyClass, setAuthorDateJustifyClass] = useState('')

    const [orientationClass, setOrientationClass] = useState(null);

    // Reset states when navigating to a new route URL
    useEffect(() => {
        setArticle(state?.article || null);
        setMainImage(state?.article ? state.article.image : {});
        setIsLoading(!state?.article);
        setIsShowComments(false);
        setNewCommentFieldVisible(false);
    }, [location.pathname]);

    useEffect(() => {
        // Determine which image object to use (from Router state or API)
        const activeImage = isLoading ? image : mainImage;

        if (activeImage?.imageName) {
            const img = new Image();
            img.src = getImageUrl(activeImage.imageName);
            
            img.onload = () => {
                // Check if vertical and set the class
                if (img.height === img.width) 
                    setOrientationClass('main-img-square')
                else
                    setOrientationClass((img.height > img.width) ? 'main-img-vertical' : 'main-img');
            };
        }
    }, [isLoading, mainImage, image]);

    const handleException = useCallback((error) => {
        dispatch(setError(MODAL_DESCRIPTION_ERROR_ARTICLE_NOT_FOUND))
        if (error.response.status === 404) {
            displayMessageModalWithParameters(MODAL_MESSAGE_ERROR, MODAL_DESCRIPTION_ERROR_ARTICLE_NOT_FOUND, MODAL_TYPE_ERROR)
        } else displayGenericErrorModal()
        return
    }, [])

    useEffect(() => {
        // If the article payload was already provided in location state, skip fetching
        if (state?.article) {
            setConditionalCssClasses(
                state.article,
                setJustifyContentContainerClass,
                setFullWidthDateClass,
                setAuthorDateJustifyClass
            );
            return;
        }

        setIsLoading(true);

        // 1. Fetch by articleId if passed through React Router state
        if (articleId) {
            getArticle(articleId)
                .then(data => {
                    setArticle(data);
                    setArticleCommentsLength(data.comments.length);
                    setIsLoading(false);
                    setMainImage(data.image);
                    setConditionalCssClasses(
                        data,
                        setJustifyContentContainerClass,
                        setFullWidthDateClass,
                        setAuthorDateJustifyClass
                    );
                })
                .catch(error => {
                    handleException(error);
                    setTimeout(() => {
                        navigate('/');
                        resetModalContext();
                    }, 3000);
                });
        } else {
            // 2. Fallback for page refreshes / direct URL hits
            // Extract the article slug cleanly from location.pathname
            const currentUrlSlug = location.pathname.substring(1);

            if (!currentUrlSlug) return;

            getArticleByUrl(currentUrlSlug)
                .then(data => {
                    setArticle(data);
                    setArticleCommentsLength(data.comments.length);
                    setIsLoading(false);
                    setMainImage(data.image);
                    setConditionalCssClasses(
                        data,
                        setJustifyContentContainerClass,
                        setFullWidthDateClass,
                        setAuthorDateJustifyClass
                    );
                })
                .catch(error => {
                    handleException(error);
                    setTimeout(() => {
                        navigate('/');
                        resetModalContext();
                    }, 3000);
                });
        }
    }, [location.pathname, articleId]);

    const articleWithCategoryName = useMemo(() => {
        return addCategoryNameToArticle(fetchCategories(), article);
    }, [state?.categories, article]);

    const clearNewComment = useCallback(() => {
        commentForm.setFieldsValue({
            "newComment": ""
        })
        setNewCommentFieldVisible(false)
    }, [setNewCommentFieldVisible, commentForm])

    const showNewCommentField = useCallback(() => {
        if (!isShowComments) {
            setIsShowComments(true)
        }
        setNewCommentFieldVisible(true)
    }, [setIsShowComments, setNewCommentFieldVisible, isShowComments])

    const addComment = useCallback(() => {
        const newComment = commentForm.getFieldValue('newComment')
        commentForm.validateFields({validateOnly: true})
        .then(() => {
            if (newComment) {
                var newCommentRequest = new FormData();
                newCommentRequest.append("userId", userId)
                newCommentRequest.append("text", newComment)
                newCommentRequest.append("articleId", article.id)
                addNewComment(newCommentRequest)
                .then(data => {
                    if (data.status === 201) {
                        displayMessageModalWithParameters(MODAL_MESSAGE_SUCCESS_REQUEST, MODAL_DESCRIPTION_SUCCESS_NEW_COMMENT, MODAL_TYPE_SUCCESS)
                        clearNewComment()
                    } else {
                        displayGenericErrorModal()
                    }
                })
                .catch(err => {
                    if (err?.response?.status === 401) {
                        displayMessageModalWithParameters(MODAL_MESSAGE_ERROR, MODAL_DESCRIPTION_ERROR_UNAUTHORIZED, MODAL_TYPE_ERROR)
                        // log the user out
                        logoutUser()
                            .then(data => {
                                clearUserContext()
                            })
                            .catch(error => {
                                displayGenericErrorModal()
                            })
                        setTimeout(() =>{
                            displayPrijavaModal()
                        }, 1000)
                    } else {
                        displayGenericErrorModal()
                    }
                setTimeout(() =>{
                    displayPrijavaModal()
                }, 1000)
                })
            }
        })
        .catch(error => error)
    }, [commentForm, clearNewComment, clearUserContext, userId, article])

    return (
        <div className="flex-container flex-column flex-grow-1">
            {article && 
                <Helmet>
                    <title>{article.title}</title>
                    <meta
                        name="description"
                        content={article.description}
                        data-react-helmet="true" 
                    />
                </Helmet>
            }
            
            <div className="flex-container center-align flex-column full-article-body">
                <h1 className="font-weight-400 text-center">
                    {isLoading && title ? 
                        title : 
                            !isLoading && article && article.title}
                </h1>

                {!isLoading && article && article.series && (
                    <Link to={"/serijali/" + article.series.url} className="article-serial-badge">
                        Dio serijala: {article.series.title}
                    </Link>
                )}

                <div className="main-img-container">
                    {/* If we have ANY image (from state or from loaded article), show it */}
                    {(image || mainImage?.imageName) ? (
                        <ZoomedImage 
                            cssClassNames={[orientationClass]} 
                            image={!isLoading ? mainImage : image} 
                            imgCssClassNames={'central-left-img'}  
                        />
                    ) : (
                        /* Only show Skeleton if we have absolutely no image data yet */
                        <Skeleton.Image active className="main-img" />
                    )
                    }
                    {/* Only show description when loading is finished */}
                    {!isLoading && mainImage?.description && (
                        <p className="full-article-image-description">{mainImage.description}</p>
                    )}
                </div>

                {/** Mozda opis? */}
                <div className={`flex-container full-width ${authorDateJustifyClass} width-98p`}>
                    {(isLoading && categoryName === 'Promo') || (!isLoading && articleWithCategoryName?.categoryName === 'Promo') ? (
                        <p className="width-70p">
                            <b>Sponzorisani članak</b>
                        </p>
                    )
                    :
                    (!isLoading && article && 
                        (article.authors && article.authors.length >= 1 ?
                            <p className="width-70p">
                                {article.authors.length === 1 ? <b>Piše: </b> : <b>Pišu: </b>}
                                {article.authors.map((author, index) => {
                                    return (<span key={author.id}>
                                        <Link to={"/autori/" + encodeURIComponent(author.url)} style={{color: 'black'}}>
                                            {author.firstName + ' ' + author.lastName}
                                        </Link>
                                        {index !== article.authors.length-1 ? ', ' : ''}
                                    </span>)
                                })}
                            </p>
                            :
                            <p className="width-70p">
                                <b>Piše:</b> Historiografija.ba
                            </p>
                        )
                    )}
                    {!isLoading && article && <p className={`text-end ${fullWidthDateClass}`}>{formatDate(article.date)}</p>}
                    {isLoading && !article && <span className="width-70p"><Skeleton avatar active paragraph={{rows: 0}}/></span>}
                    {isLoading && formattedDate && <p className="text-end width-30p">{formattedDate}</p>}
                    {isLoading && !formattedDate && <span className="text-end width-30p"><Skeleton active paragraph={{rows: 0}} title={{width: '100%'}}/></span>}
                </div>

                {!isLoading && article ? <div className="full-article-text ql-editor no-pre-wrap" dangerouslySetInnerHTML={{__html: purify.sanitize(article.text, { 
                    ADD_TAGS: ["iframe"], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling']
                })}}>
                </div>
                :
                <Skeleton active paragraph={{rows: 5}}/>
                }

                {!isLoading && article && article.sponsor && (
                    <div className="article-sponsor-note">
                        <span className="article-sponsor-label">Objavu je omogućio/la:</span>
                        {(article.sponsor.websiteUrl || article.sponsor.url) ? (
                            <a
                                href={article.sponsor.websiteUrl || article.sponsor.url}
                                target="_blank"
                                rel="noreferrer"
                                className="article-sponsor-link"
                            >
                                <img
                                    src={getImageUrl(article.sponsor.logo.imageName)}
                                    alt={article.sponsor.logo.altText}
                                    className="article-sponsor-logo"
                                />
                                <span>{article.sponsor.name}</span>
                            </a>
                        ) : (
                            <span className="article-sponsor-link">
                                <img
                                    src={getImageUrl(article.sponsor.logo.imageName)}
                                    alt={article.sponsor.logo.altText}
                                    className="article-sponsor-logo"
                                />
                                <span>{article.sponsor.name}</span>
                            </span>
                        )}
                    </div>
                )}
                
            </div>

            {!isLoading && article && article.series && (article.series.previous || article.series.next) && (
                <div className="serial-navigation width-80-body">
                    {article.series.previous ? (
                        <Link
                            to={"/" + article.series.previous.url}
                            state={{
                                articleId: article.series.previous.id,
                                title: article.series.previous.title
                            }}
                            className="serial-nav-item serial-nav-prev"
                        >
                            <span className="serial-nav-direction">← Prethodni članak</span>
                            <span className="serial-nav-title">{article.series.previous.title}</span>
                        </Link>
                    ) : (
                        <span className="serial-nav-item serial-nav-placeholder" aria-hidden="true" />
                    )}

                    {article.series.next ? (
                        <Link
                            to={"/" + article.series.next.url}
                            state={{
                                articleId: article.series.next.id,
                                title: article.series.next.title
                            }}
                            className="serial-nav-item serial-nav-next"
                        >
                            <span className="serial-nav-direction">Sljedeći članak →</span>
                            <span className="serial-nav-title">{article.series.next.title}</span>
                        </Link>
                    ) : (
                        <span className="serial-nav-item serial-nav-placeholder" aria-hidden="true" />
                    )}
                </div>
            )}

            {/** KOMENTARI */}
            {!isLoading && article && <div className='flex-container flex-column width-80-body'>
                <p><b>Komentari ({articleCommentsLength})</b></p>
                {!userRole && <p>Prijavite se da biste mogli komentarisati.</p>}
                {userRole && 
                        /** If there are no comments, and the user is logged in */
                        <Button onClick={showNewCommentField} 
                            className="login-form-button full-width main-color-background text-white">
                            Novi komentar
                        </Button>
                }
                {newCommentFieldVisible && 
                    <div className="flex-container flex-column">
                        <Form form={commentForm} className="top-margin-5 flex-container flex-column" onFinish={addComment}>
                            <Form.Item name="newComment"
                                rules={[
                                    () => ({
                                        validator(_, value) {
                                            if (value) {
                                                if (value.length > 1000) {
                                                    return Promise.reject(new Error('Tekst je predug'))
                                                }
                                                return Promise.resolve()
                                            }
                                            return Promise.reject(new Error('Tekst je obavezno polje'))
                                        }
                                    })
                                ]}>
                                <Input.TextArea
                                    placeholder="Napišite komentar ovdje" 
                                    className="comment-input"
                                /> 
                            </Form.Item>
                            <Form.Item className="flex-container flex-end-item space-between">
                                <Button type="default" onClick={clearNewComment}>
                                    Odustanite
                                </Button>
                                <Button htmlType="submit" className="main-color-background text-white">
                                    Spasite komentar
                                </Button>
                            </Form.Item>
                        </Form>
                        
                    </div>
                }

                {((article && article.comments && articleCommentsLength !== 0) && 
                        <div className="grey-section">
                            {
                                (article.comments.map((comment) => {
                                    return <Comment
                                            key={comment.id}
                                            commentId={comment.id}
                                            text={comment.text}
                                            username={comment.user.username}
                                            likes={comment.numberOfLikes}
                                            dislikes={comment.numberOfDislikes}
                                            date={formatDateWithTime(comment.postedDate)}
                                            liked={comment.liked}
                                            disliked={comment.disliked}
                                            />
                                }))
                            }
                        </div>
                    )
                }
            </div>
            }  

            {/** POVEZANI ČLANCI */}
            {!isLoading && article && article.relatedArticles && article.relatedArticles.length !== 0 &&
                <div className='flex-container flex-column width-80-body'>
                    <p><b>Povezani članci</b></p>
                    <List 
                        itemLayout="horizontal"
                        dataSource={article.relatedArticles}
                        renderItem={(item, index) => (
                            <Link 
                                to={"/" + item.url} 
                                key={item.id}
                                state={{ 
                                    articleId: item.id,
                                    title: item.title,
                                    image: item.image,
                                    url: item.url,
                                    categoryName: item.categoryName
                                }}
                            >
                                <List.Item className="border-solid-lavender padding-2p">
                                    <List.Item.Meta
                                        avatar={<Avatar 
                                            src={getImageUrl(item.image.imageName)}
                                            alt={item.image.altText} 
                                        />}
                                        title={item.title}
                                        description={item.description}
                                    />
                                </List.Item>
                            </Link>
                        )}
                        bordered={true}
                        className="bottom-margin-5p"
                    />
                </div>
            }
        </div>
    )
})


export default Article